import { v4 as uuidv4 } from "uuid";
import stringSimilarity from "string-similarity";

export const generateUUID = () => {
  const newUUID = uuidv4();
  return newUUID;
};

export const generateUniqueNumber = () => {
  const min = 1000000000; // Smallest 10-digit number
  const max = 9999999999; // Largest 10-digit number
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

// Hex to RGBA helper
export const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export function toSentenceCase(str) {
  if (!str || typeof str !== "string") return "";
  const trimmed = str.trim();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

export const isNonEmptyString = (val) =>
  typeof val === "string" && val.trim() !== "";

export const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let browser = "Unknown";
  let browserVersion = "Unknown";

  if (ua.includes("Chrome") && !ua.includes("Edg")) {
    browser = "Chrome";
    browserVersion = ua.match(/Chrome\/([0-9.]+)/)?.[1] || "Unknown";
  } else if (ua.includes("Firefox")) {
    browser = "Firefox";
    browserVersion = ua.match(/Firefox\/([0-9.]+)/)?.[1] || "Unknown";
  } else if (ua.includes("Safari") && !ua.includes("Chrome")) {
    browser = "Safari";
    browserVersion = ua.match(/Version\/([0-9.]+)/)?.[1] || "Unknown";
  } else if (ua.includes("Edg")) {
    browser = "Edge";
    browserVersion = ua.match(/Edg\/([0-9.]+)/)?.[1] || "Unknown";
  } else if (ua.includes("Opera")) {
    browser = "Opera";
    browserVersion = ua.match(/Opera\/([0-9.]+)/)?.[1] || "Unknown";
  }

  return { browser, browserVersion };
};

export const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  let device = "Desktop";
  let os = "Unknown";

  if (
    /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)
  ) {
    device = "Mobile";
  } else if (/iPad/i.test(ua)) {
    device = "Tablet";
  }

  if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Mac")) os = "macOS";
  else if (ua.includes("Linux")) os = "Linux";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("iOS")) os = "iOS";

  return {
    device,
    os,
    userAgent: ua,
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      colorDepth: window.screen.colorDepth,
    },
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    memory: navigator.deviceMemory || "Unknown",
    cores: navigator.hardwareConcurrency || "Unknown",
  };
};

export const getClientIP = async () => {
  try {
    const response = await fetch("https://api.ipify.org?format=json", {
      timeout: 3000,
    });
    const data = await response.json();
    return data.ip || "Unknown";
  } catch {
    return "Unknown";
  }
};

export const getSessionInfo = () => {
  let pageLoadTime = 0;

  const navigationEntries = performance.getEntriesByType("navigation");

  if (navigationEntries.length > 0) {
    const nav = navigationEntries[0];
    pageLoadTime = nav.loadEventEnd;
  }

  return {
    sessionId: sessionStorage.getItem("session_id") || "unknown",
    userId: localStorage.getItem("user_id") || "anonymous",
    visitId: sessionStorage.getItem("visit_id") || `visit_${Date.now()}`,
    pageLoadTime,
  };
};

export const getErrorSeverity = (status) => {
  if (!status) return "error";
  if (status >= 500) return "critical";
  if (status >= 400) return "error";
  if (status >= 300) return "warning";
  return "info";
};

export const getNetworkInfo = () => {
  return {
    network_status: navigator.onLine,
    connection: navigator.connection
      ? {
          effective_type: navigator.connection.effectiveType,
          downlink: navigator.connection.downlink,
          rtt: navigator.connection.rtt,
        }
      : null,
  };
};

export const getPageInfo = () => {
  return {
    url: window.location.href,
    pathname: window.location.pathname,
    referrer: document.referrer || "Direct",
    language: navigator.language || "Unknown",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Unknown",
  };
};

export function cleanTranscript(text, threshold = 0.9) {
  if (!text) return "";
  const sentences = text
    .split(/[।!?]+/)
    .map((s) => s.trim())
    .filter(Boolean);

  const result = [];

  for (const sentence of sentences) {
    const isDuplicate = result.some((existing) => {
      const similarity = stringSimilarity.compareTwoStrings(existing, sentence);
      return similarity >= threshold;
    });

    if (!isDuplicate) result.push(sentence);
  }

  return result.join("। ") + "।";
}

export function isValidString(value) {
  if (value === null || value === undefined) return false;
  if (typeof value !== "string") return false;

  return value.trim() !== "";
}

export function isValidArray(value) {
  if (!Array.isArray(value)) return false;

  return value.length > 0;
}

export function isValidObject(value) {
  if (value === null || value === undefined) return false;
  if (typeof value !== "object" || Array.isArray(value)) return false;

  return Object.keys(value).length > 0;
}

export function isValidFieldForm(data) {
  if (!data || typeof data !== "object") return false;

  const findInputFields = (value) => {
    if (!value || typeof value !== "object") return null;

    // 🎯 Exact key match only
    if (Array.isArray(value.InputFields)) {
      return value.InputFields;
    }

    // 🔍 Recurse through object keys
    for (const key of Object.keys(value)) {
      const result = findInputFields(value[key]);
      if (result) return result;
    }

    return null;
  };

  const inputFields = findInputFields(data);

  // 1️⃣ Must exist & not be empty
  if (!Array.isArray(inputFields) || inputFields.length === 0) {
    return false;
  }

  // 2️⃣ Must contain at least one valid field
  return inputFields.some(
    (field) =>
      field &&
      typeof field === "object" &&
      typeof field.FieldName === "string" &&
      field.FieldName.trim().length > 0
  );
}


export function isFormAvailable(response) {
  const TARGET_VALUE = "only form fields";
  const isLog=false;

  const log = (reason, extra = {}) => {
    if (isLog) {
      console.info("[isFormAvailable]", reason, extra);
    }
  };

  if (!response || typeof response !== "object") {
    log("Invalid response object", { response });
    return false;
  }

  // 🔍 Recursive value scanner (key-agnostic)
  const containsTargetValue = (value, path = "") => {
    if (typeof value === "string") {
      if (value.trim().toLowerCase() === TARGET_VALUE) {
        log("Matched target string value", { path, value });
        return true;
      }
      return false;
    }

    if (Array.isArray(value)) {
      return value.some((item, index) =>
        containsTargetValue(item, `${path}[${index}]`)
      );
    }

    if (value && typeof value === "object") {
      return Object.keys(value).some((key) =>
        containsTargetValue(value[key], path ? `${path}.${key}` : key)
      );
    }

    return false;
  };

  // 1️⃣ Text-based detection (anywhere)
  if (containsTargetValue(response)) {
    return true;
  }

  // 2️⃣ Structural form detection (strict)
  const inputFields = response?.field_form?.InputFields;

  if (Array.isArray(inputFields)) {
    const validFields = inputFields.filter(
      (field) =>
        field &&
        typeof field === "object" &&
        (typeof field.FieldName === "string" ||
          typeof field.name === "string" ||
          typeof field.label === "string")
    );

    if (validFields.length > 0) {
      log("Detected valid form fields", {
        total: inputFields.length,
        valid: validFields.length,
      });
      return true;
    }

    log("InputFields present but no valid field definitions found", {
      inputFields,
    });
  }

  log("Form not available in response");
  return false;
}


export const generateCustomUUID = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  // Fallback for older mobile browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};
